import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { SmartphoneIcon, Volume2Icon, ArrowRightIcon, AlertTriangleIcon, ShieldIcon, EyeIcon, CheckCircle2Icon, XCircleIcon, HelpCircleIcon, PrinterIcon, MailIcon, LinkIcon, QrCodeIcon } from 'lucide-react';
// --- Slide Animation Variants ---
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};
export function ScamPhishingModulePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  // Part 3 State
  const [practiceAnswer, setPracticeAnswer] = useState<'correct' | 'incorrect' | null>(null);
  // Part 4 State
  const [reflectionAnswer, setReflectionAnswer] = useState<string | null>(null);
  // Part 5 State
  const [taskChecked, setTaskChecked] = useState(false);
  const [showProof, setShowProof] = useState(false);
  const [safeWord, setSafeWord] = useState('');
  const [completed, setCompleted] = useState(false);
  const nextStep = () => {
    if (step < 5) {
      setDirection(1);
      setStep(step + 1);
    }
  };
  const prevStep = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(step - 1);
    }
  };
  const getStepName = () => {
    switch (step) {
      case 1:
        return 'Context';
      case 2:
        return 'What to Know';
      case 3:
        return 'Try It';
      case 4:
        return 'Your Household';
      case 5:
        return 'Your Action';
      default:
        return '';
    }
  };
  return <div className={`min-h-screen flex flex-col font-sans overflow-hidden ${step === 1 ? 'bg-[#1a0647]' : 'bg-[#f8f8f8]'}`} data-id="element-5106" data-mp="340q96jad8yxr">
      {/* --- Persistent Header --- */}
      <header className="px-6 py-4 z-20 relative" data-id="element-5107" data-mp="31epkaxahdgd4">
        <div className="flex items-center justify-between mb-3" data-id="element-5108" data-mp="31g0misoa2g24">
          <p className="text-[#01ced1] font-bold text-sm tracking-wider uppercase" data-id="element-5109" data-mp="36dalp01gy635">
            Step {step} of 5 — {getStepName()}
          </p>
          {step > 1 && <button onClick={prevStep} className={`text-sm font-bold ${step === 1 ? 'text-white' : 'text-gray-500'}`} data-id="element-5110" data-mp="2wfw4uzbmvfab">
              Back
            </button>}
        </div>
        <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden" data-id="element-5111" data-mp="2nfpyryi9lhl6">
          <motion.div className="h-full bg-[#01ced1]" initial={{
          width: '0%'
        }} animate={{
          width: `${step / 5 * 100}%`
        }} transition={{
          duration: 0.3
        }} data-id="element-5112" data-mp="28qtn7rh01mft" />
        </div>
      </header>

      {/* --- Main Content Area --- */}
      <main className="flex-1 relative" data-id="element-5113" data-mp="23tl51qvupiek">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-5114" data-mp="1l2ohzv6vnvbj">
          {/* --- PART 1: HOOK --- */}
          {step === 1 && <motion.div key="step1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 px-6 py-8 flex flex-col" data-id="element-5115" data-mp="1g5oe9qxi34nl">
              <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full" data-id="element-5116" data-mp="1z2fzi5wlrtua">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-8 relative" data-id="element-5117" data-mp="2ctumsoxjmh75">
                  <SmartphoneIcon size={32} className="text-white" data-id="element-5118" data-mp="28uvklc374yje" />
                  <div className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full border-2 border-[#1a0647]" data-id="element-5119" data-mp="2mtbq4zpboizy"></div>
                </div>

                <h1 className="text-3xl font-bold text-white mb-6 leading-tight" data-id="element-5120" data-mp="2hrmdxgqd5zaa">
                  It happened to a family just like yours.
                </h1>

                <p className="text-lg text-[#d0bdf5] mb-6 leading-relaxed" data-id="element-5121" data-mp="35vua4hhu18v9">
                  Dan got a call from someone claiming to be his bank. They knew
                  his name and last four digits. He transferred $3,200 before
                  realizing it was a scam.
                </p>

                <p className="text-xl font-bold text-[#01ced1] mb-10" data-id="element-5122" data-mp="2iy0sdtg76lqa">
                  Sound familiar?
                </p>

                <button className="flex items-center gap-3 bg-white/10 text-white px-5 py-3 rounded-xl w-fit hover:bg-white/20 transition-colors" data-id="element-5123" data-mp="2n0dqpwsm7hd1">
                  <Volume2Icon size={20} data-id="element-5124" data-mp="2wwqrtfqhoxht" />
                  <span className="font-medium" data-id="element-5125" data-mp="2wxs23hzolfyh">Listen to this story</span>
                </button>
              </div>

              <div className="mt-auto pt-8 pb-safe max-w-md mx-auto w-full flex justify-end" data-id="element-5126" data-mp="79j6diz8r8ka">
                <button onClick={nextStep} className="w-14 h-14 rounded-full bg-[#01ced1] text-[#1a0647] flex items-center justify-center hover:bg-[#00b8bc] transition-colors shadow-lg" data-id="element-5127" data-mp="c82t3llnsodp">
                  <ArrowRightIcon size={24} data-id="element-5128" data-mp="1mxpdidwm60e1" />
                </button>
              </div>
            </motion.div>}

          {/* --- PART 2: CONCEPT --- */}
          {step === 2 && <motion.div key="step2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 px-6 py-8 flex flex-col overflow-y-auto" data-id="element-5129" data-mp="1dy2lujtos8y6">
              <div className="max-w-md mx-auto w-full pb-24" data-id="element-5130" data-mp="2yckzkkxlb44m">
                <h1 className="text-2xl font-bold text-[#1a0647] mb-8 leading-tight" data-id="element-5131" data-mp="2tet095um8fqe">
                  Scammers use one trick above all others: urgency.
                </h1>

                <div className="space-y-6 mb-8" data-id="element-5132" data-mp="rnkglzqopsnj">
                  {/* Concept 1 */}
                  <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200" data-id="element-5133" data-mp="wheycxmqbxjz">
                    <div className="flex items-center gap-3 mb-4" data-id="element-5134" data-mp="ht0xgs8vij62">
                      <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0" data-id="element-5135" data-mp="mrc7p432yvot">
                        <AlertTriangleIcon size={20} className="text-red-500" data-id="element-5136" data-mp="1ah6f364mrs1o" />
                      </div>
                      <h3 className="font-bold text-[#111111] text-lg" data-id="element-5137" data-mp="1feg62q7o4brm">
                        They pressure you to act NOW
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5138" data-mp="15fqxxp5kq5ok">
                      <div className="bg-red-50 p-3 rounded-lg border border-red-100" data-id="element-5139" data-mp="3m5fntnljxxvy">
                        <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1 block" data-id="element-5140" data-mp="3m7sb8ga2x0ge">
                          Scam
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-5141" data-mp="3ilnj4lknesb0">
                          "Your account will be suspended in 24 hours if you
                          don't verify."
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-100" data-id="element-5142" data-mp="3nmupqhb21lg3">
                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-1 block" data-id="element-5143" data-mp="2zx0eg46dw4xy">
                          Real
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-5144" data-mp="2uvt6o548r5ra">
                          "Your statement is ready to view online."
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Concept 2 */}
                  <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200" data-id="element-5145" data-mp="2h57wr5wrm65j">
                    <div className="flex items-center gap-3 mb-4" data-id="element-5146" data-mp="2c6d1h4hm2p4j">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-5147" data-mp="1qod1k71l37mp">
                        <ShieldIcon size={20} className="text-blue-500" data-id="element-5148" data-mp="1vmej2njd2yw2" />
                      </div>
                      <h3 className="font-bold text-[#111111] text-lg" data-id="element-5149" data-mp="20m13bgxlxfed">
                        They claim to be someone official
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5150" data-mp="29nh5x2i6kd81">
                      <div className="bg-red-50 p-3 rounded-lg border border-red-100" data-id="element-5151" data-mp="29kux6zjjzro8">
                        <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1 block" data-id="element-5152" data-mp="2ek7lloq2p6sx">
                          Scam
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-5153" data-mp="2oak295urofts">
                          "This is the IRS. You owe back taxes."
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-100" data-id="element-5154" data-mp="2xbs1bq53xkox">
                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-1 block" data-id="element-5155" data-mp="lppvood81mhh">
                          Real
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-5156" data-mp="coi933k2ia38">
                          The IRS will only contact you via physical mail first.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Concept 3 */}
                  <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200" data-id="element-5157" data-mp="14hsh0d0t5qy2">
                    <div className="flex items-center gap-3 mb-4" data-id="element-5158" data-mp="10g74wtw3lgox">
                      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0" data-id="element-5159" data-mp="vm2we676p0lq">
                        <EyeIcon size={20} className="text-purple-500" data-id="element-5160" data-mp="qo14qwvozsyn" />
                      </div>
                      <h3 className="font-bold text-[#111111] text-lg" data-id="element-5161" data-mp="1o4zveatyawc5">
                        They know just enough to seem real
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5162" data-mp="1mowp90xuv5ch">
                      <div className="bg-red-50 p-3 rounded-lg border border-red-100" data-id="element-5163" data-mp="1hpjza7boti5o">
                        <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1 block" data-id="element-5164" data-mp="1vojp3iaxs33f">
                          Scam
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-5165" data-mp="1rpasm5oihr46">
                          "Hi John, regarding your recent Amazon order ending in
                          4921..."
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-100" data-id="element-5166" data-mp="2fdalpc9aptlx">
                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-1 block" data-id="element-5167" data-mp="2ady0evy2oulg">
                          Real
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-5168" data-mp="2ob4dqb01213a">
                          Data breaches make this info public. Don't trust it.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <button className="flex items-center gap-3 bg-white border border-gray-200 text-[#111111] px-5 py-3 rounded-xl w-full justify-center hover:bg-gray-50 transition-colors shadow-sm mb-8" data-id="element-5169" data-mp="37yqt2irtgw80">
                  <Volume2Icon size={20} className="text-[#8528e2]" data-id="element-5170" data-mp="2xzi4k5fq09t1" />
                  <span className="font-medium" data-id="element-5171" data-mp="2k9el1m886u7a">Listen to a 30-second tip</span>
                </button>

                <button onClick={nextStep} className="w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md" data-id="element-5172" data-mp="2p1g71zv73zai">
                  Continue
                </button>
              </div>
            </motion.div>}

          {/* --- PART 3: PRACTICE --- */}
          {step === 3 && <motion.div key="step3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 px-6 py-8 flex flex-col overflow-y-auto" data-id="element-5173" data-mp="268vgip2yy69v">
              <div className="max-w-md mx-auto w-full pb-24" data-id="element-5174" data-mp="3u8z5z8mawt9s">
                <h1 className="text-2xl font-bold text-[#1a0647] mb-8" data-id="element-5175" data-mp="ut2lxhklpkyc">
                  Is this a scam?
                </h1>

                {/* Simulated Phone Screen */}
                <div className="bg-white rounded-[2rem] border-8 border-gray-200 shadow-lg p-4 mb-8 relative overflow-hidden" data-id="element-5176" data-mp="prn2fty2nmi2">
                  <div className="w-20 h-5 bg-gray-200 rounded-b-xl absolute top-0 left-1/2 -translate-x-1/2" data-id="element-5177" data-mp="gtb1oz3hr8bw"></div>
                  <div className="pt-6 pb-2 border-b border-gray-100 flex items-center justify-center mb-4" data-id="element-5178" data-mp="36j4vuytd9it8">
                    <span className="font-bold text-sm text-gray-800" data-id="element-5179" data-mp="31i7hyyfe3axj">
                      Messages
                    </span>
                  </div>
                  <div className="bg-gray-100 rounded-2xl p-4 text-sm text-gray-800 relative" data-id="element-5180" data-mp="1m02ju35ns94y">
                    <p data-id="element-5181" data-mp="1q1o449kivpp6">
                      ALERT: Your Chase account has been locked. Click here to
                      restore access:{' '}
                      <span className="text-blue-500 underline" data-id="element-5182" data-mp="1ut64owimvo9m">
                        chase-secure-alert.net/verify
                      </span>
                    </p>
                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gray-100 rounded-full" data-id="element-5183" data-mp="24q0zp5fp76j5"></div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-5184" data-mp="29qexlunl503g">
                  <button onClick={() => setPracticeAnswer('correct')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${practiceAnswer === 'correct' ? 'bg-[#8528e2] text-white ring-4 ring-purple-200' : 'bg-[#8528e2] text-white hover:bg-[#7322c4]'}`} data-id="element-5185" data-mp="2dqyyn9ba4nuv">
                    Yes, this is a scam
                  </button>
                  <button onClick={() => setPracticeAnswer('incorrect')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all border-2 ${practiceAnswer === 'incorrect' ? 'border-red-500 text-red-600 bg-red-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`} data-id="element-5186" data-mp="3g73lfa9sejlh">
                    Looks legitimate
                  </button>
                </div>

                {/* Feedback Area */}
                <AnimatePresence data-id="element-5187" data-mp="2wgrlp2b1s1ti">
                  {practiceAnswer === 'correct' && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5" data-id="element-5188" data-mp="3b91tdzudv9xv">
                      <div className="flex items-start gap-3" data-id="element-5189" data-mp="t8r66i655hxs">
                        <CheckCircle2Icon className="text-green-600 shrink-0 mt-0.5" size={24} data-id="element-5190" data-mp="y1am3lsawp5r" />
                        <div data-id="element-5191" data-mp="ob7lp0iuo8xe">
                          <h3 className="font-bold text-green-800 mb-1" data-id="element-5192" data-mp="oafwq8jrd9g3">
                            Great catch!
                          </h3>
                          <p className="text-sm text-green-700 mb-4" data-id="element-5193" data-mp="1bypf10hrjf2h">
                            The URL "chase-secure-alert.net" is fake, and banks
                            will never text you a link to unlock your account.
                          </p>
                          <button onClick={nextStep} className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-green-700 transition-colors" data-id="element-5194" data-mp="17y5l05shkrkk">
                            Continue
                          </button>
                        </div>
                      </div>
                    </motion.div>}

                  {practiceAnswer === 'incorrect' && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5" data-id="element-5195" data-mp="1oimdh9uxtkj4">
                      <div className="flex items-start gap-3" data-id="element-5196" data-mp="15k0fw4ymqbjy">
                        <HelpCircleIcon className="text-amber-600 shrink-0 mt-0.5" size={24} data-id="element-5197" data-mp="15md3axn5pe4e" />
                        <div data-id="element-5198" data-mp="2zy1d6lej6qhx">
                          <h3 className="font-bold text-amber-800 mb-1" data-id="element-5199" data-mp="2zw8blkirywgs">
                            Good instinct to pause.
                          </h3>
                          <p className="text-sm text-amber-700" data-id="element-5200" data-mp="hof7suk434fu">
                            Here's what to look for: The link says
                            "chase-secure-alert.net" instead of just
                            "chase.com". Banks will never text you a link to log
                            in. Try again!
                          </p>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>}

          {/* --- PART 4: REFLECTION --- */}
          {step === 4 && <motion.div key="step4" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 px-6 py-8 flex flex-col overflow-y-auto" data-id="element-5201" data-mp="7t5482e2eozy">
              <div className="max-w-md mx-auto w-full pb-24" data-id="element-5202" data-mp="zqcgcjjka2zd">
                <h1 className="text-2xl font-bold text-[#1a0647] mb-2" data-id="element-5203" data-mp="uux8q9c5d3be">
                  Think about your household.
                </h1>
                <p className="text-lg text-gray-600 mb-8" data-id="element-5204" data-mp="18mv5rlsl38r7">
                  Who in your family is most likely to receive a message like
                  this?
                </p>

                <div className="space-y-3 mb-8" data-id="element-5205" data-mp="1ilb1k22k3671">
                  {['Me', 'My parents', 'My kids', 'Everyone'].map(option => <button key={option} onClick={() => setReflectionAnswer(option)} className={`w-full p-5 rounded-xl border-2 text-left font-bold text-lg transition-all ${reflectionAnswer === option ? 'border-[#8528e2] bg-[#f0ebf8] text-[#8528e2]' : 'border-gray-200 bg-white text-[#111111] hover:border-[#8528e2] hover:bg-gray-50'}`} data-id="element-5206" data-mp="1rjmn3pokfdsm">
                      {option}
                    </button>)}
                </div>

                <AnimatePresence data-id="element-5207" data-mp="3j69mjyvx7zfp">
                  {reflectionAnswer && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-8" data-id="element-5208" data-mp="35gfw5c4rp73v">
                      <p className="text-blue-800 font-medium" data-id="element-5209" data-mp="2mhbq8c8ivtgf">
                        {reflectionAnswer === 'My parents' ? 'Parents are the most targeted group for financial scams. Module 8 covers this in detail.' : reflectionAnswer === 'My kids' ? "Kids are often targeted through social media and gaming platforms. We'll cover that soon." : 'Scammers cast a wide net. Everyone is a target, which is why family-wide awareness is key.'}
                      </p>
                    </motion.div>}
                </AnimatePresence>

                {reflectionAnswer && <motion.button initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} onClick={nextStep} className="w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md" data-id="element-5210" data-mp="1o9lllhkfhpmw">
                    Continue
                  </motion.button>}
              </div>
            </motion.div>}

          {/* --- PART 5: TAKEAWAY + VERIFICATION --- */}
          {step === 5 && <motion.div key="step5" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 flex flex-col overflow-y-auto bg-white" data-id="element-5211" data-mp="vlz9fkx4xcof">
              <div className="h-2 w-full bg-[#01ced1]" data-id="element-5212" data-mp="t1azhow23tmr"></div>
              <div className="px-6 py-8 max-w-md mx-auto w-full pb-32" data-id="element-5213" data-mp="5anr72i8b1zo">
                <h1 className="text-2xl font-bold text-[#1a0647] mb-6" data-id="element-5214" data-mp="c43oxnzli57">
                  Your one action right now.
                </h1>

                <div className="bg-[#f8f8f8] p-6 rounded-2xl border border-gray-200 mb-8" data-id="element-5215" data-mp="f21wjqwv2w0q">
                  <h2 className="text-xl font-bold text-[#111111] mb-4" data-id="element-5216" data-mp="aa0axedszsy1">
                    Set up your family safe word.
                  </h2>
                  <p className="text-gray-600 mb-6" data-id="element-5217" data-mp="3inz6iewv5cb0">
                    A word only your family knows to verify real emergencies. If
                    someone calls claiming to be a family member in trouble, ask
                    for the safe word.
                  </p>

                  {!showProof && <div className="space-y-6" data-id="element-5218" data-mp="b8z8p472770s">
                      <label className="flex items-start gap-4 cursor-pointer group" data-id="element-5219" data-mp="g33gtqs29l3g">
                        <div className="relative flex items-center justify-center mt-1" data-id="element-5220" data-mp="l1erg3q6jzmq">
                          <input type="checkbox" className="sr-only" checked={taskChecked} onChange={e => setTaskChecked(e.target.checked)} data-id="element-5221" data-mp="3kgs4h93rq3yx" />
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${taskChecked ? 'bg-[#8528e2] border-[#8528e2]' : 'border-gray-300 group-hover:border-[#8528e2]'}`} data-id="element-5222" data-mp="2anhp12a677t">
                            {taskChecked && <CheckCircle2Icon size={16} className="text-white" data-id="element-5223" data-mp="2y84ky5jv3jtf" />}
                          </div>
                        </div>
                        <span className="text-lg font-bold text-[#111111]" data-id="element-5224" data-mp="2jiyogpces7p8">
                          I have agreed on a safe word with my family.
                        </span>
                      </label>

                      <AnimatePresence data-id="element-5225" data-mp="2aiad62eeglg1">
                        {taskChecked && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} className="space-y-6 overflow-hidden" data-id="element-5226" data-mp="2akmzerr6xhyw">
                            <p className="text-sm text-gray-500 italic" data-id="element-5227" data-mp="6ugym6wr50by">
                              Before you confirm: this step matters for your
                              family's protection. Please only mark it done if
                              you actually completed it. It's completely okay to
                              come back to this later.
                            </p>
                            <button onClick={() => setShowProof(true)} className="w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md" data-id="element-5228" data-mp="3k829dmf7aa0d">
                              Mark as Complete
                            </button>
                          </motion.div>}
                      </AnimatePresence>
                    </div>}

                  {showProof && !completed && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="space-y-4" data-id="element-5229" data-mp="2mmr803hly1p0">
                      <label className="block text-sm font-bold text-[#111111]" data-id="element-5230" data-mp="2qnkwaxyypiif">
                        What is your family's safe word?
                      </label>
                      <p className="text-xs text-gray-500 mb-2" data-id="element-5231" data-mp="30n1yh5f09e9n">
                        We won't store it — this is just to confirm you set one
                        up.
                      </p>
                      <input type="text" value={safeWord} onChange={e => setSafeWord(e.target.value)} className="w-full p-4 rounded-xl border border-gray-300 focus:border-[#8528e2] focus:ring-2 focus:ring-purple-200 outline-none text-lg" placeholder="Enter safe word" data-id="element-5232" data-mp="3abtmx3kuxgft" />
                      <button disabled={!safeWord.trim()} onClick={() => setCompleted(true)} className="w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5233" data-mp="2l0fdxgl6bvog">
                        Verify
                      </button>
                    </motion.div>}

                  {completed && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="text-center py-4" data-id="element-5234" data-mp="38s31boj88olz">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4" data-id="element-5235" data-mp="1bs2hcmqtq8j3">
                        <CheckCircle2Icon size={32} className="text-green-600" data-id="element-5236" data-mp="171cdxf1ykfr1" />
                      </div>
                      <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-5237" data-mp="x3y6g4y0j1av">
                        Verified Complete!
                      </h3>
                      <div className="inline-block bg-[#01ced1] text-[#1a0647] font-bold px-4 py-2 rounded-full text-sm mb-6" data-id="element-5238" data-mp="1uqf2l6v0f8v1">
                        +50 pts
                      </div>

                      <div className="bg-white rounded-xl p-4 border border-gray-200 mb-6 text-left" data-id="element-5239" data-mp="1krfqtfey6x3t">
                        <p className="text-sm font-bold text-[#111111] mb-3" data-id="element-5240" data-mp="1gqvsw9mjvpgm">
                          Share this task:
                        </p>
                        <div className="flex justify-between gap-2" data-id="element-5241" data-mp="5ci8xfux3u5v">
                          <button className="flex-1 flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-gray-600" data-id="element-5242" data-mp="5ecyx8l1qcgt">
                            <PrinterIcon size={20} data-id="element-5243" data-mp="29gml0a7lye3n" />
                            <span className="text-[10px] font-medium" data-id="element-5244" data-mp="29hejbsslapxm">
                              Print
                            </span>
                          </button>
                          <button className="flex-1 flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-gray-600" data-id="element-5245" data-mp="20g7y56ezwqy1">
                            <MailIcon size={20} data-id="element-5246" data-mp="2o73vej280eco" />
                            <span className="text-[10px] font-medium" data-id="element-5247" data-mp="2s6ukmic3mmf3">
                              Email
                            </span>
                          </button>
                          <button className="flex-1 flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-gray-600" data-id="element-5248" data-mp="2je130e96jay2">
                            <LinkIcon size={20} data-id="element-5249" data-mp="372tyk9lw3gs5" />
                            <span className="text-[10px] font-medium" data-id="element-5250" data-mp="3bulhpz2puk89">
                              Copy
                            </span>
                          </button>
                          <button className="flex-1 flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-gray-600" data-id="element-5251" data-mp="3l7kn4nejq2ei">
                            <QrCodeIcon size={20} data-id="element-5252" data-mp="3uyxdfakp4kif" />
                            <span className="text-[10px] font-medium" data-id="element-5253" data-mp="3uyp0xjshjh7r">QR</span>
                          </button>
                        </div>
                      </div>

                      <div className="bg-purple-50 rounded-xl p-4 border border-purple-100 mb-8 text-left" data-id="element-5254" data-mp="329rj1hs6rmfj">
                        <p className="text-sm text-purple-900 font-medium mb-3" data-id="element-5255" data-mp="2y9zgblph4ai3">
                          Dan completed this step too. Want to let him know?
                        </p>
                        <button className="w-full py-2 bg-white border border-purple-200 text-purple-700 rounded-lg font-bold text-sm hover:bg-purple-100 transition-colors" data-id="element-5256" data-mp="379cs4l12lb5z">
                          Send High Five ✋
                        </button>
                      </div>

                      <Link to="/dashboard" className="block w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md no-underline" data-id="element-5257" data-mp="2bjtwxjhnqtcr">
                        Continue to Next Action
                      </Link>
                    </motion.div>}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </main>

      {/* --- Persistent 'I'm Stuck' Button --- */}
      <div className="fixed bottom-6 left-6 z-30" data-id="element-5258" data-mp="2q9hx2he3rlyw">
        <button className="bg-white text-gray-600 border border-gray-200 shadow-lg rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-gray-50 transition-colors" data-id="element-5259" data-mp="2lhzyfzzjxxh3">
          <HelpCircleIcon size={18} data-id="element-5260" data-mp="3dykebo4trcsv" />
          I'm Stuck
        </button>
      </div>
    </div>;
}